// RenderFunctions.tsx
import React, { useEffect, useState } from 'react';
import { Modal, Button, Form, Dropdown } from 'react-bootstrap';
import { X } from 'react-bootstrap-icons';
import { FaEye, FaEyeSlash } from 'react-icons/fa';
import { toast } from 'react-toastify';
import {
  createCategory,
} from '../../../../../../../../../../crud/menu_api/categories';
import { getWorkstationsByLocalId } from '../../../../../../../../../../crud/management_api/workstations';
import { getAllMenuCategoryTypes } from '../../../../../../../../../../crud/menu_api/menu_category_types';
import './CreateCategory.scss';

export interface WorkstationOption {
  id: number;
  name: string;
}

export interface MenuOption {
  id: number;
  name: string;
}

interface CreateCategoryProps {
  show: boolean;
  onClose: () => void;
  menu: any;
  menus: any;
  onCategoryCreated?: (category: any) => void;
  setSelectedMenu: (menu: any) => void;
  setMenus: (menus: any[]) => void;
  parentCategoryId?: number;
}

export const getWorkstationsOptions = async () => {
  const userContext = localStorage.getItem('user');
  const localId = userContext != null ? JSON.parse(userContext).local_id : 0;
  const response = await getWorkstationsByLocalId(localId);
  if (response?.data) {
    return response.data.map((ws: any) => ({
      id: ws.workstation_id,
      name: ws.workstation_name,
    })) as WorkstationOption[];
  }
  return [];
};

const CreateCategory: React.FC<CreateCategoryProps> = ({
  show,
  onClose,
  menu,
  menus,
  onCategoryCreated,
  setSelectedMenu,
  setMenus,
  parentCategoryId,
}) => {
  const [workstations, setWorkstations] = useState<WorkstationOption[]>([]);
  const [selectedWorkstationId, setSelectedWorkstationId] = useState<number | null>(null);

  const [menuCategoryTypes, setMenuCategoryTypes] = useState<any[]>([]);
  const [selectedMenuCategoryTypeId, setSelectedMenuCategoryTypeId] = useState<number>(0);

  const [isMain, setIsMain] = useState(true);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (show) {
      getWorkstationsOptions().then(setWorkstations);
      getAllMenuCategoryTypes()
        .then((resp: any) => setMenuCategoryTypes(resp?.data || []))
        .catch(() => setMenuCategoryTypes([]));
    }
  }, [show]);

  const handleFormSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);

    const userContext = localStorage.getItem('user');
    const localId = userContext ? JSON.parse(userContext).local_id : 0;
    const w_id = selectedWorkstationId === null || selectedWorkstationId === 0
      ? null
      : selectedWorkstationId;

    const create_payload = {
      category_id: 0,
      category_name: (e.currentTarget as any).category_name.value,
      is_main: isMain,
      menu_id: menu?.menu_id || 0,
      workstation_id: w_id,
      local_id: localId,
      state: true,
      menu_category_type_id: selectedMenuCategoryTypeId,
      parent_category_id: parentCategoryId || null,
    };

    createCategory(create_payload)
      .then((resp) => {
        toast.success('Categoría creada con éxito!');
        onClose();

        const created = (resp as any)?.data;
        if (onCategoryCreated) onCategoryCreated(created);

        if (created) {
          // update local state with complete category structure
          const newCat = { 
            id: created.category_id, 
            name: created.category_name,
            subcategories: [], // Initialize empty subcategories array
            items: [] // Initialize empty items array
          };
          const updatedCats = Array.isArray(menu?.categories)
            ? [...menu.categories, newCat]
            : [newCat];

          setSelectedMenu({ ...menu, categories: updatedCats });

          const updatedMenus = menus.map((m: any) =>
            m.menu_id === menu.menu_id
              ? { ...m, categories: updatedCats }
              : m
          );
          setMenus(updatedMenus);
        }
      })
      .catch((err) => {
        console.error(err);
        toast.error('Error inesperado al crear la categoría');
      })
      .finally(() => setIsLoading(false));
  };

  return (
    <Modal show={show}
           onHide={onClose}
           centered 
           className="create-category-modal">
      <Form onSubmit={handleFormSubmit}>
        <Modal.Header>
          <div className="form-modal-title-container">
            <Modal.Title className="form-modal-title">
              Crear Categoría
            </Modal.Title>
          </div>
          <Button variant="none"
                  className="modal-close-button"
                  onClick={onClose}>
            <X size={24} />
          </Button>
        </Modal.Header>

        <Modal.Body>
          <div className="form-container">
            <Form.Group controlId="category_name" className="mp-formgroup horizontal">
              <Form.Label>Nombre</Form.Label>
              <Form.Control
                type="text"
                name="category_name"
                placeholder="Ingresa el nombre de la categoría"
                required
              />
            </Form.Group>

            {/* Workstation Dropdown */}
            <Form.Group className="mp-formgroup horizontal">
              <Form.Label>Estación de trabajo</Form.Label>
              <Dropdown
                onSelect={(eventKey: string | null) =>
                  setSelectedWorkstationId(eventKey ? Number(eventKey) : null)
                }
              >
                <Dropdown.Toggle
                  variant="light"
                  id="dropdown-workstation"
                  className="dropdown-toggle"
                >
                  {selectedWorkstationId
                    ? workstations.find(w => w.id === selectedWorkstationId)?.name
                    : 'Seleccionar estación'}
                </Dropdown.Toggle>
                <Dropdown.Menu>
                  <Dropdown.Item eventKey="0">
                    -- Ninguna --
                  </Dropdown.Item>
                  {workstations.map(ws => (
                    <Dropdown.Item key={ws.id} eventKey={ws.id.toString()}>
                      {ws.name}
                    </Dropdown.Item>
                  ))}
                </Dropdown.Menu>
              </Dropdown>
            </Form.Group>

            {/* Visibility Options */}
            <Form.Group className="mp-formgroup horizontal">
              <div>
                <Form.Label>Visibilidad del menú</Form.Label>
                <Form.Text className="text-muted">
                  Elige si la categoría será visible para los clientes
                </Form.Text>
              </div>
              <div className="visibility-options">
                <div 
                  className={`visibility-option visible ${isMain ? 'selected' : ''}`}
                  onClick={() => setIsMain(true)}
                >
                  <FaEye className="option-icon" />
                  <span className="option-text">Visible</span>
                </div>
                <div 
                  className={`visibility-option hidden ${!isMain ? 'selected' : ''}`}
                  onClick={() => setIsMain(false)}
                >
                  <FaEyeSlash className="option-icon" />
                  <span className="option-text">Oculta</span>
                </div>
              </div>
            </Form.Group>

            {/* Category Type Dropdown */}
            <Form.Group className="mp-formgroup horizontal">
              <Form.Label>Tipo de categoría</Form.Label>
              <Dropdown
                onSelect={(eventKey: string | null) =>
                  setSelectedMenuCategoryTypeId(eventKey ? Number(eventKey) : 0)
                }
              >
                <Dropdown.Toggle
                  variant="light"
                  id="dropdown-category-type"
                  className="dropdown-toggle"
                >
                  {selectedMenuCategoryTypeId
                    ? menuCategoryTypes.find((t: any) => t.menu_category_type_id === selectedMenuCategoryTypeId)?.name
                    : 'Seleccionar tipo'}
                </Dropdown.Toggle>
                <Dropdown.Menu>
                  <Dropdown.Item eventKey="0">-- Seleccionar tipo --</Dropdown.Item>
                  {menuCategoryTypes.map((type: any) => (
                    <Dropdown.Item
                      key={type.menu_category_type_id}
                      eventKey={type.menu_category_type_id.toString()}
                    >
                      {type.name}
                    </Dropdown.Item>
                  ))}
                </Dropdown.Menu>
              </Dropdown>
            </Form.Group>
          </div>
        </Modal.Body>

        <Modal.Footer>
          <Button variant="none"
                  onClick={onClose}
                  className="form-button-cancel">
            Cancelar
          </Button>
          <Button variant="none"
                  type="submit"
                  className="form-button-create"
                  disabled={isLoading || !selectedMenuCategoryTypeId || !selectedWorkstationId}
          >
            {isLoading ? (
              <>
                <span
                  className="spinner-border spinner-border-sm"
                  role="status"
                  aria-hidden="true"
                ></span>{' '}
                Creando...
              </>
            ) : (
              'Crear'
            )}
          </Button>
        </Modal.Footer>
      </Form>
    </Modal>
  );
};

export default CreateCategory;
